import {
    NOT_AVAILABLE,
    formatMetric,
} from '~/containers/workflows/verify/segmetricsColumns';
import { RoiStatistics } from '~/engine/util/algorithms/sdf/ContourStatistics';
import { type AxisKey } from '~/utils/units';

const MM2_PER_CM2 = 100;
const MM_PER_CM = 10;

/** One axis component of a vector statistic. */
export interface AxisValue {
    readonly axis: AxisKey;
    /** Formatted value in the row's display unit (cm); no unit suffix. */
    readonly value: string;
}

/**
 * A row of the contour-statistics list. Scalar rows carry a single formatted
 * value; axis rows carry one labeled component per patient axis (X/Y/Z) so the
 * coordinate frame is shown explicitly alongside the unit (docs/ui_specs.md:
 * always cite units and coordinate systems on clinical values). The label id
 * carries the display unit.
 */
export type StatRow =
    | {
          readonly kind: 'scalar';
          readonly labelId: string;
          readonly value: string;
      }
    | {
          readonly kind: 'axes';
          readonly labelId: string;
          readonly axes: readonly AxisValue[];
      };

const AXES: readonly AxisKey[] = ['x', 'y', 'z'];

/**
 * Format three mm values as cm (2 dp), one labeled component per patient axis.
 * Returns null when any component is missing or non-finite, so the caller can
 * fall back to a single "not available" cell rather than a partial triplet.
 */
function formatAxisTripletCm(
    values: readonly number[] | null
): AxisValue[] | null {
    if (!values) {
        return null;
    }
    const formatted = values.map((mm) => formatMetric(mm, 2, MM_PER_CM));
    if (formatted.some((part) => part === null)) {
        return null;
    }
    return AXES.map((axis, i) => ({ axis, value: formatted[i] as string }));
}

function scalarRow(labelId: string, value: string | null): StatRow {
    return { kind: 'scalar', labelId, value: value ?? NOT_AVAILABLE };
}

/** An axis row, or a "not available" scalar cell when the triplet is null. */
function axisRow(labelId: string, axes: AxisValue[] | null): StatRow {
    return axes
        ? { kind: 'axes', labelId, axes }
        : { kind: 'scalar', labelId, value: NOT_AVAILABLE };
}

/**
 * Maps engine SDF statistics to display rows. Surface area mm²→cm² (2 dp),
 * sphericity unitless (3 dp), center of mass mm→cm (2 dp) and bounding-box
 * extent mm→cm (2 dp) as per-axis (X/Y/Z) components. Unit conversion lives
 * here at the UI boundary (docs/ui_specs.md); volume is shown separately and
 * not repeated. Pure → unit-testable.
 */
export function roiStatRows(stats: RoiStatistics): StatRow[] {
    const com = stats.centerOfMassMm;
    const bbox = stats.boundingBox;
    return [
        scalarRow(
            'roi.stats.surfaceArea',
            formatMetric(stats.surfaceAreaMm2, 2, MM2_PER_CM2)
        ),
        scalarRow('roi.stats.sphericity', formatMetric(stats.sphericity, 3)),
        axisRow(
            'roi.stats.centerOfMass',
            formatAxisTripletCm(com ? [com[0], com[1], com[2]] : null)
        ),
        axisRow(
            'roi.stats.boundingBox',
            formatAxisTripletCm(
                bbox ? AXES.map((_, i) => bbox.max[i] - bbox.min[i]) : null
            )
        ),
    ];
}
